import {
  getVisibleWorkflowQuestions,
  getWorkflowForCodeType,
  mergeWithSystemAttributes
} from '@codametrix/shared-views';

/**
 * Checks if a rubric is assigned to the project.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {boolean} - True if a rubric is assigned.
 */
const hasRubricAssigned = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const rubricVersionId =
    gradeBenchPanelState.qaRule?.data?.project_configuration?.rubricVersionId;
  return rubricVersionId !== null && rubricVersionId !== undefined;
};

/**
 * Checks if all required visible questions for a code have been answered.
 *
 * @param {Accelerate.CQACode} code - The code to check.
 * @param {CQA.WorkflowQuestion[] | undefined} workflowQuestions - Workflow questions for this code type.
 * @returns {boolean} - True if all required visible questions have answers.
 */
const areRequiredQuestionsAnswered = (
  code: Accelerate.CQACode,
  workflowQuestions: CQA.WorkflowQuestion[] | undefined
): boolean => {
  // If no workflow questions, consider it satisfied (code from scratch case)
  if (!workflowQuestions || workflowQuestions.length === 0) {
    return true;
  }

  const codeAttributes = code.attributes || [];

  // Merge with system question attributes for workflow calculations
  const allAttributes = mergeWithSystemAttributes(
    codeAttributes,
    workflowQuestions,
    code.original
  );

  // Get visible questions based on current answers and triggers
  const visibleQuestions = getVisibleWorkflowQuestions(
    workflowQuestions,
    allAttributes
  );

  // Filter to only required questions
  const requiredQuestions = visibleQuestions.filter(
    (wq: CQA.WorkflowQuestion) => wq.question.required
  );

  // Check if all required questions have answers
  return requiredQuestions.every((wq: CQA.WorkflowQuestion) =>
    codeAttributes.some(attr => attr.questionId === wq.question.id)
  );
};

/**
 * Checks if all required visible case-level questions have been answered.
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The grade bench panel state.
 * @returns {boolean} - True if all required case questions have answers, or if no case workflow exists.
 */
const areRequiredCaseQuestionsAnswered = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const rubricVersion = gradeBenchPanelState.rubricVersion;
  const caseAttributes = gradeBenchPanelState.caseAttributes || [];

  if (!rubricVersion) {
    return true;
  }

  const caseWorkflow = getWorkflowForCodeType(rubricVersion, 'case');

  // If no case workflow, consider it satisfied
  if (!caseWorkflow?.questions || caseWorkflow.questions.length === 0) {
    return true;
  }

  const visibleQuestions = getVisibleWorkflowQuestions(
    caseWorkflow.questions,
    caseAttributes
  );

  const requiredQuestions = visibleQuestions.filter(
    (wq: CQA.WorkflowQuestion) => wq.question.required
  );

  return requiredQuestions.every((wq: CQA.WorkflowQuestion) =>
    caseAttributes.some(attr => attr.questionId === wq.question.id)
  );
};

/**
 * Determines if all CQA work items in the given grade bench panel state are evaluated.
 * Uses the project configuration to determine which codes and assessment fields are required.
 *
 * When a rubric is assigned:
 * - Validates that all required rubric questions are answered (instead of role/assessment)
 *
 * When no rubric is assigned:
 * - Uses legacy validation checking role and assessment fields
 *
 * @param {Accelerate.GradeBenchPanelState} gradeBenchPanelState - The state of the grade bench panel containing QA rules and invoices.
 * @returns {boolean} - Returns true if all work items are evaluated, otherwise false.
 */
export const isCQAWorkItemEvaluated = (
  gradeBenchPanelState: Accelerate.GradeBenchPanelState
): boolean => {
  const projectConfiguration =
    gradeBenchPanelState.qaRule.data.project_configuration;
  const invoices = gradeBenchPanelState.invoices;
  const rubricVersion = gradeBenchPanelState.rubricVersion;
  const rubricAssigned = hasRubricAssigned(gradeBenchPanelState);

  // If rubric is assigned but attributes haven't been fetched yet, wait
  if (rubricAssigned && !gradeBenchPanelState.attributesFetched) {
    return false;
  }

  // Check case-level required questions if rubric is assigned
  if (rubricAssigned && !areRequiredCaseQuestionsAnswered(gradeBenchPanelState)) {
    return false;
  }

  /**
   * Checks if a given code is evaluated.
   * For rubric-based validation: checks required workflow questions.
   * For legacy validation: checks assessment and role fields.
   *
   * @param {Accelerate.CQACode} code - The code to evaluate.
   * @param {string} codeType - The type of code ('procedure', 'diagnosis', 'modifier').
   * @returns {boolean} - Returns true if the code is evaluated, otherwise false.
   */
  const isCodeEvaluated = (
    code: Accelerate.CQACode,
    codeType: string
  ): boolean => {
    if (rubricAssigned) {
      // Rubric-based validation: check required questions
      const workflow = getWorkflowForCodeType(rubricVersion, codeType);
      return areRequiredQuestionsAnswered(code, workflow?.questions);
    }

    // Legacy behavior: check assessment and role
    if (projectConfiguration.assessment && !code.codeAssessment?.assessment) {
      return false;
    }
    if (projectConfiguration.role && !code.codeAssessment?.role) {
      return false;
    }
    return true;
  };

  /**
   * Checks if all codes in the given array are evaluated.
   *
   * @param {Accelerate.CQACode[]} codes - The array of codes to evaluate.
   * @param {string} codeType - The type of codes.
   * @returns {boolean} - Returns true if all codes are evaluated, otherwise false.
   */
  const checkIfCodesEvaluated = (
    codes: Accelerate.CQACode[],
    codeType: string
  ): boolean => {
    return codes.every(c => isCodeEvaluated(c, codeType));
  };

  return invoices.every(invoice =>
    invoice.transactions.every(transaction => {
      if (
        projectConfiguration['procedure'] &&
        !checkIfCodesEvaluated(transaction.procedures, 'procedure')
      ) {
        return false;
      }
      if (
        projectConfiguration['diagnosis'] &&
        !checkIfCodesEvaluated(transaction.diagnoses, 'diagnosis')
      ) {
        return false;
      }
      if (
        projectConfiguration['modifier'] &&
        !checkIfCodesEvaluated(transaction.modifiers, 'modifier')
      ) {
        return false;
      }
      return true;
    })
  );
};
